import React from 'react';
import { Alert, Button, LinearProgress, Tooltip, Typography } from '@mui/material';
import { AlertTriangle, Info, Layers } from 'lucide-react';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './FacetsTab.module.scss';
import {
  coverageTone,
  formatCoverage,
  formatDate,
  formatNumber,
  recordCount,
  recordNoun,
} from '../kgUtils';

/**
 * Facets and health (screen 7) — the same catalog the agent's `kg_describe` sees.
 *
 * The discipline this screen exists to enforce: `coverage_pct` next to every
 * count. At 48% coverage, "1,204 SEBI papers" means 1,204 of the records that
 * say who issued them — the true total is unknowable, not larger by a known
 * amount. A count without its coverage is exactly the failure the whole feature
 * is meant to prevent.
 */
const FacetsTab = ({ dataset, facets, goToTab }) => {
  if (!facets) {
    // "No data yet" and "the catalog request failed" are different problems and
    // must not share a message — telling someone with 48,000 records to go and
    // load some sends them to the wrong place entirely.
    const empty = recordCount(dataset) === 0;
    return (
      <div className={sharedStyles.emptyState}>
        <Layers size={40} className={sharedStyles.emptyIcon} />
        <Typography variant="h6">
          {empty ? 'No facet catalog yet' : "Couldn't load the facet catalog"}
        </Typography>
        <Typography variant="body2" className={sharedStyles.muted}>
          {empty
            ? 'Facets are derived when records are ingested. Load some data and they will appear here.'
            : 'The records are there, but their facet summary could not be fetched just now. Reload the page to try again.'}
        </Typography>
        <Button
          className={sharedStyles.outlineBtn}
          variant="outlined"
          onClick={() => (empty ? goToTab('data') : window.location.reload())}
          sx={{ mt: 2 }}
        >
          {empty ? 'Load records' : 'Reload'}
        </Button>
      </div>
    );
  }

  const list = facets.facets || [];
  const total = Number(facets.total_records || 0);
  // With no records loaded, EVERY facet reads 0% — which means "nothing pushed
  // yet", not "your mapping is broken". Calling that out in red would send
  // someone off to fix a mapping that is perfectly fine.
  const hasRecords = total > 0;
  const zeroCoverage = hasRecords
    ? list.filter((f) => Number(f.coverage_pct || 0) === 0)
    : [];

  return (
    <div className={styles.wrap}>
      <div className={styles.summary}>
        <div>
          <span className={styles.summaryValue}>{formatNumber(total)}</span>
          <span className={styles.summaryLabel}>{recordNoun(dataset, total)}</span>
        </div>
        <div>
          <span className={styles.summaryValue}>{list.length}</span>
          <span className={styles.summaryLabel}>facet{list.length === 1 ? '' : 's'}</span>
        </div>
        {facets.date_range?.from && (
          <div>
            <span className={styles.summaryValue}>
              {formatDate(facets.date_range.from)} – {formatDate(facets.date_range.to)}
            </span>
            <span className={styles.summaryLabel}>date range</span>
          </div>
        )}
      </div>

      {/* A facet at 0% is the highest-value signal in the product: it means the
          column is empty, or the path doesn't resolve. */}
      {zeroCoverage.length > 0 && (
        <Alert severity="error" className={styles.zeroAlert}>
          <strong>
            {zeroCoverage.length} facet{zeroCoverage.length === 1 ? ' is' : 's are'} never
            populated
          </strong>
          <p>
            {zeroCoverage.map((f) => f.name).join(', ')} — the column is empty in every record, or
            the path doesn’t resolve to anything. Remember that a column name containing a space,
            hyphen or dot has to be backtick-quoted.
          </p>
          <Button size="small" variant="text" onClick={() => goToTab('mapping')}>
            Check the mapping
          </Button>
        </Alert>
      )}

      {!hasRecords && (
        <Alert severity="info" className={styles.zeroAlert}>
          <strong>No records loaded yet.</strong>
          <p>
            These {list.length} facets are configured and ready — coverage and values appear
            once you push data. Nothing here is wrong.
          </p>
          <Button size="small" variant="text" onClick={() => goToTab('data')}>
            Load records
          </Button>
        </Alert>
      )}

      {hasRecords && (
        <Typography variant="caption" className={styles.staleNote}>
          <Info size={12} /> Coverage percentages can lag by up to five minutes. Names and values
          refresh as soon as the data or mapping changes.
        </Typography>
      )}

      <div className={styles.grid}>
        {list.map((facet) => {
          const pct = Number(facet.coverage_pct || 0);
          const values = facet.values || [];
          return (
            <div key={facet.name} className={styles.card}>
              <div className={styles.cardHead}>
                <div className={styles.cardTitle}>
                  <span className={styles.facetLabel}>{facet.label || facet.name}</span>
                  <code className={styles.facetName}>{facet.name}</code>
                </div>
                <Tooltip
                  title={
                    facet.cardinality === 'many'
                      ? 'A record can carry several of these values.'
                      : 'A record carries at most one of these values.'
                  }
                >
                  <span className={styles.cardTag}>
                    {facet.cardinality === 'many' ? 'several' : 'one'}
                  </span>
                </Tooltip>
              </div>

              {hasRecords ? (
                <>
                  <div className={styles.coverageRow}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, pct)}
                      className={`${styles.coverageBar} ${styles[coverageTone(pct)]}`}
                    />
                    <span
                      className={`${styles.coverageValue} ${pct === 0 ? styles.coverageZero : ''}`}
                    >
                      {formatCoverage(pct)}
                    </span>
                  </div>
                  <span className={styles.coverageHelp}>
                    {pct === 0 ? (
                      <>
                        <AlertTriangle size={12} /> Never populated — check the mapping
                      </>
                    ) : pct >= 99.5 ? (
                      <>Recorded on essentially every record.</>
                    ) : (
                      <>
                        {/* The API gives the raw numerator, so say it out loud —
                            "1,204 of 48,213" lands harder than a percentage. */}
                        Recorded on{' '}
                        {facet.records_with_value !== undefined
                          ? `${formatNumber(facet.records_with_value)} of ${formatNumber(total)} records`
                          : `${formatCoverage(pct)} of records`}
                        . Counts over this facet are a floor, not a total — the rest simply
                        don’t say.
                      </>
                    )}
                  </span>
                </>
              ) : (
                <span className={styles.coverageHelp}>Waiting for data.</span>
              )}

              {/* `note` is a free-text caveat from the mapping config, shown
                  verbatim: for fields that are populated and still misleading. */}
              {facet.note && (
                <div className={styles.note}>
                  <Info size={13} />
                  <span>{facet.note}</span>
                </div>
              )}

              {values.length > 0 && (
                <div className={styles.values}>
                  {values.map((v) => (
                    <div key={String(v.value)} className={styles.valueRow}>
                      <span className={styles.valueName} title={String(v.value)}>
                        {String(v.value)}
                      </span>
                      <span className={styles.valueCount}>{formatNumber(v.count)}</span>
                    </div>
                  ))}
                  {/* Truncated is not "these are the values". */}
                  {facet.more_values && (
                    <span className={styles.moreValues}>
                      Top {values.length} shown — there are more values than this.
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FacetsTab;
